'use client';

import { AlertSeverity } from 'builder/components/enums/AlertSeverityEnum';
import { RecaptchaV3Action } from 'google-recaptcha/components';
import { RecaptchaDisclaimer } from 'google-recaptcha/components';
import { useFormWithRecaptchaV3 } from 'google-recaptcha/components';
import React, { type JSX, useCallback, useState } from 'react';

import { Alert, Button } from '@videoblocks/storywind';

import { requestResetAction } from './requestResetAction';

export function ResetTokenForm(): JSX.Element {
    const [bannerMessage, setBannerMessage] = useState('');

    const onSubmit = useCallback(
        async (formData: FormData, recaptchaToken?: string) => {
            const email = formData.get('email') as string;
            await requestResetAction(email, recaptchaToken ?? '');
            setBannerMessage(
                `Password reset instructions have been sent to ${email}. If you receive nothing, make sure the email you used is actually registered with a Storyblocks account.`,
            );
        },
        [setBannerMessage],
    );

    const onError = useCallback(() => {}, []);

    const submitForm = useFormWithRecaptchaV3({
        onSubmit,
        onError,
        action: RecaptchaV3Action.REQUEST_PASSWORD_RESET,
    });

    return (
        <>
            {bannerMessage && (
                <Alert severity={AlertSeverity.success} onClose={() => setBannerMessage('')}>
                    {bannerMessage}
                </Alert>
            )}
            <div className="max-w-2xl w-full filter drop-shadow-xl rounded-md p-10 bg-white flex flex-col items-center sm:p-0 sm:drop-shadow-none sm:max-w-none">
                <h1 className="text-lg font-semibold text-left mt-0 mr-0 mb-4">Reset password for Storyblocks</h1>
                <p className="text-left">
                    Please enter the email associated with your account and we will email you a link to reset your
                    password.
                </p>
                <form aria-label="Request Password Reset Form" className="w-[20rem] m-0" onSubmit={submitForm}>
                    <div className="text-left text-base flex flex-col mb-4">
                        <label htmlFor="email" className="font-semibold text-sm">
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            name="email"
                            required
                            placeholder="e.g. me@email.com"
                            autoComplete="email"
                            className="w-full border border-solid border-old-silver rounded h-10 pl-4"
                        />
                    </div>
                    <div className="text-center mb-4">
                        <Button id="request-reset-btn" type="submit" className="w-full">
                            Send reset link
                        </Button>
                    </div>
                    <RecaptchaDisclaimer className="text-center" />
                    <p className="text-center flex flex-col font-semibold">
                        <a href="/login" className="text-blue-700 hover:underline">
                            Go back to login
                        </a>
                    </p>
                </form>
            </div>
        </>
    );
}
